import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Button,
  ListItem,
  ListItemText,
  ListSubheader,
  List,
  ListItemButton,
} from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Satellite } from '../../types';
import { useCreateSatellite } from './api/createSatellite';
import { useSatelliteDatabase } from './api/getSatelliteDatabase';
import { useUpdateSatellite } from './api/updateSatellite';
import { SatelliteForm } from './types';

interface Props {
  open: boolean;
  handleClose: () => void;
  satellite?: Satellite;
}

const initialFormState: SatelliteForm = {
  id: undefined,
  name: '',
  satcat_number: '',
  class_of_orbit: '',
};

const SatelliteDialog = ({ open, handleClose, satellite }: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<SatelliteForm>({ defaultValues: initialFormState });
  const createSatellite = useCreateSatellite({});
  const updateSatellite = useUpdateSatellite({});
  const { data: satelliteDatabase } = useSatelliteDatabase({});
  const fuse = new Fuse(satelliteDatabase || [], {
    keys: ['officialName', 'alternativeNames'],
    includeScore: true,
  });
  let filteredSatelliteDatabase = fuse
    .search(watch('name'))
    .sort((a, b) => (a.score && b.score ? a.score - b.score : 0))
    .slice(0, 5)
    .map((resultObject) => resultObject.item);

  useEffect(() => {
    if (satellite) {
      setValue('id', satellite.id);
      setValue('name', satellite.name);
      setValue('class_of_orbit', satellite.class_of_orbit);
      setValue('satcat_number', satellite.satcat_number);
    } else reset(initialFormState);
  }, [satellite, open, reset, setValue]);
  const handleCancel = () => {
    reset(initialFormState);
    handleClose();
  };
  const onSubmit = async () => {
    const formState = getValues();
    const id = watch('id');
    if (!id) {
      await createSatellite.mutateAsync({
        ...formState,
        satcat_number: formState.satcat_number
          ? Number(formState.satcat_number)
          : undefined,
      });
    } else {
      await updateSatellite.mutateAsync({
        data: {
          ...formState,
          class_of_orbit: formState.class_of_orbit
            ? formState.class_of_orbit
            : null,
          satcat_number: formState.satcat_number
            ? Number(formState.satcat_number)
            : null,
        },
        satelliteId: id,
      });
    }
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {watch('id') ? 'Edit Satellite' : 'New Satellite'}
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction='column'
          style={{ paddingTop: '.4rem' }}
        >
          <Grid item xs>
            <Controller
              name='name'
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='name'
                  label='Satellite Name'
                  name='name'
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  fullWidth
                  required
                  error={!!errors.name}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='satcat_number'
              control={control}
              rules={{ required: false, min: 1 }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='satcat_number'
                  label='SATCAT Number'
                  name='satcat_number'
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  fullWidth
                  inputProps={{ type: 'number' }}
                  error={!!errors.satcat_number}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='class_of_orbit'
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel id='orbit-select-label'>
                    Class of Orbit
                  </InputLabel>
                  <Select
                    labelId='orbit-select-label'
                    label='Class of Orbit'
                    id='class_of_orbit'
                    name='class_of_orbit'
                    defaultValue=''
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                  >
                    <MenuItem value={''}>None</MenuItem>
                    <MenuItem value={'LEO'}>LEO</MenuItem>
                    <MenuItem value={'GEO'}>GEO</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Grid item>
          <List>
            <ListSubheader>Possible Matches</ListSubheader>
            {filteredSatelliteDatabase &&
            filteredSatelliteDatabase.length > 0 ? (
              filteredSatelliteDatabase.map((satellite, idx) => (
                <ListItemButton
                  key={idx}
                  onClick={() => {
                    const formState = getValues();
                    reset({
                      ...formState,
                      name: satellite.officialName,
                      satcat_number: satellite.noradNumber,
                      class_of_orbit: satellite.classOfOrbit,
                    });
                  }}
                >
                  <ListItemText primary={satellite.officialName} />
                </ListItemButton>
              ))
            ) : (
              <ListItem>
                Type in the name of a satellite to see possible matches.
              </ListItem>
            )}
          </List>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit(onSubmit)}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SatelliteDialog;
